import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent';
import { PageHeader } from '../components/templates/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Badge } from '../components/atoms/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { Inline } from '../components/atoms/Inline';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Select } from '../components/atoms/Select/Select';
import { Stack } from '../components/atoms/Stack/Stack';
import { Text } from '../components/atoms/Typography/Text';
import { VoiceRecorderButton } from '../components/atoms/VoiceRecorderButton';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { EmergencyChecklist } from '../components/incidents/EmergencyChecklist/EmergencyChecklist';
import { useOptionalCapture } from '../components/capture/CaptureContext';
import { parameterLabel } from '../components/measurements/measurementHistoryUtils';
import { EMERGENCY_SITUATIONS, isEmergencySituation, type EmergencySituationKey } from '../content/emergency/situations';
import { CONTENT_REVIEWED } from '../content/reviewStatus';
import { getLatestMeasurements, type LatestParameter } from '../services/measurementService';
import {
  createIncident,
  incidentsKey,
  listInterventions,
  updateIncident,
  type ChecklistProgressItem,
  type Incident,
} from '../services/incidentService';
import { getTimeline } from '../services/timelineService';
import { useTranslation } from '../i18n/useTranslation';
import './EmergencyPage.css';

const WEEK_MS = 7 * 86_400_000;

type ValueState = 'out' | 'in' | 'unknown';

function valueState(p: LatestParameter): ValueState {
  if (!p.target) return 'unknown';
  return p.latest.value < p.target.min || p.latest.value > p.target.max ? 'out' : 'in';
}

/**
 * Emergency mode (docs/voiceFeature.md, UC-18; wireframe "Emergency Mode"):
 * a calm first-aid checklist per situation, the safety note for the human
 * first, the tank's values and changes of the last 7 days, and the mentor with
 * this context. The checklist is bundled with the app (works offline). No
 * incident is created on open: the first tick or "Log incident" creates one,
 * anchored at the time the page was opened; ticks are saved with their time.
 */
export function EmergencyPage() {
  const { t, i18n } = useTranslation();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const capture = useOptionalCapture();

  const fromUrl = searchParams.get('situation');
  const [situation, setSituation] = useState<EmergencySituationKey>(isEmergencySituation(fromUrl) ? fromUrl : 'fish_distress');
  const [ticked, setTicked] = useState<Record<string, string>>({});
  const [incident, setIncident] = useState<Incident | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const openedAt = useRef(new Date().toISOString());
  /** One token per visit: a double tap (or a retry) creates one incident only. */
  const clientToken = useRef(crypto.randomUUID());

  const since = useMemo(() => new Date(Date.now() - WEEK_MS).toISOString(), []);
  const latest = useQuery({ queryKey: ['measurements', aquariumId, 'latest'], queryFn: () => getLatestMeasurements(aquariumId), enabled: aquariumId !== '' });
  const interventions = useQuery({
    queryKey: ['interventions', aquariumId, 'emergency', since],
    queryFn: () => listInterventions(aquariumId, { from: since }),
    enabled: aquariumId !== '',
  });
  const timeline = useQuery({
    queryKey: ['timeline', aquariumId, 'emergency'],
    queryFn: () => getTimeline(aquariumId, { types: 'livestock,dosing', limit: 30 }),
    enabled: aquariumId !== '',
  });

  const recentValues = (latest.data ?? []).filter((p) => Date.parse(p.latest.measuredAt) >= Date.parse(since));
  const changes = [
    ...(interventions.data ?? []).map((i) => ({
      id: `i-${i.id}`,
      at: i.occurredAt,
      text: String(t(`incidents.interventionKinds.${i.kind}`)) + (i.note ? ` — ${i.note}` : ''),
    })),
    ...(timeline.data?.data.events ?? [])
      .filter((e) => Date.parse(e.occurredAt) >= Date.parse(since))
      .map((e) => ({ id: `t-${e.id}`, at: e.occurredAt, text: e.title })),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  const progress = (state: Record<string, string>): ChecklistProgressItem[] =>
    Object.entries(state).map(([step, at]) => ({ step, at }));

  /** Creates the incident on first use, else saves the ticks; the checklist keeps working offline. */
  const persist = async (state: Record<string, string>, current: EmergencySituationKey): Promise<Incident | null> => {
    setSaving(true);
    try {
      const saved = incident
        ? await updateIncident(aquariumId, incident.id, { situation: current, checklist_progress: progress(state) })
        : await createIncident(aquariumId, {
            anchor_at: openedAt.current,
            situation: current,
            checklist_progress: progress(state),
            client_token: clientToken.current,
          });
      setIncident(saved);
      setSaveFailed(false);
      void queryClient.invalidateQueries({ queryKey: incidentsKey(aquariumId) });
      return saved;
    } catch {
      setSaveFailed(true);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const toggle = (step: string, checked: boolean): void => {
    const next = { ...ticked };
    if (checked) next[step] = new Date().toISOString();
    else delete next[step];
    setTicked(next);
    setAnnouncement(String(t(checked ? 'emergency.announceTicked' : 'emergency.announceUnticked', { step: t(`emergency.situations.${situation}.steps.${step}`) })));
    void persist(next, situation);
  };

  const changeSituation = (value: string): void => {
    if (!isEmergencySituation(value)) return;
    setSituation(value);
    setTicked({});
    if (incident) void persist({}, value);
  };

  const logIncident = async (): Promise<void> => {
    const saved = await persist(ticked, situation);
    if (saved) setAnnouncement(String(t('emergency.created')));
  };

  const askMentor = async (): Promise<void> => {
    const saved = incident ?? (await persist(ticked, situation));
    navigate(`/aquariums/${encodeURIComponent(aquariumId)}/mentor`, {
      state: {
        draft: String(t('emergency.mentorDraft', { situation: t(`emergency.situations.${situation}.label`) })),
        ...(saved ? { incidentId: saved.id } : {}),
      },
    });
  };

  const situationOptions = EMERGENCY_SITUATIONS.map((s) => ({ value: s.key, label: String(t(`emergency.situations.${s.key}.label`)) }));
  const label = (p: LatestParameter): string => parameterLabel(t, p.parameterId, p.parameterName);
  const fmt = (n: number): string => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(n);
  const when = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(iso));

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="narrow">
          <PageHeader
            title={t('emergency.title')}
            description={incident ? t('emergency.created') : t('emergency.subtitle')}
            actions={
              <Button variant="primary" onClick={() => void askMentor()} isLoading={saving && !incident}>
                {t('emergency.askMentor')}
              </Button>
            }
          />
          <Stack gap="8">
            {!CONTENT_REVIEWED.emergencyChecklists && (
              <Alert tone="warning" live="off" title={t('emergency.reviewPendingTitle')} data-testid="review-pending">
                {t('emergency.reviewPendingBody')}
              </Alert>
            )}
            <Card padding="lg">
              <Stack gap="4">
                <Select
                  label={String(t('emergency.situation'))}
                  value={situation}
                  onChange={(e) => changeSituation(e.target.value)}
                  options={situationOptions}
                />
                <Alert tone="warning" live="off" title={t('emergency.safetyTitle')}>
                  {t(`emergency.situations.${situation}.safety`)}
                </Alert>
              </Stack>
            </Card>

            <Section title={t('emergency.checklistTitle')}>
              <Card padding="lg">
                <Stack gap="4">
                  <EmergencyChecklist situation={situation} ticked={ticked} onToggle={toggle} />
                  {saveFailed && (
                    <Alert tone="warning" compact live="polite">
                      {t('emergency.saveFailed')}
                    </Alert>
                  )}
                  <Inline gap="2" justify="end">
                    {!incident && (
                      <Button variant="secondary" onClick={() => void logIncident()} isLoading={saving}>
                        {t('emergency.logIncident')}
                      </Button>
                    )}
                    {incident && (
                      <Button variant="ghost" onClick={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/incidents/${encodeURIComponent(incident.id)}`)}>
                        {t('emergency.openIncident')}
                      </Button>
                    )}
                  </Inline>
                </Stack>
              </Card>
            </Section>

            <Section title={t('emergency.valuesTitle')}>
              <Card padding="lg">
                {latest.isLoading ? (
                  <LiveRegion busy>
                    <Text>{t('emergency.valuesLoading')}</Text>
                  </LiveRegion>
                ) : latest.isError ? (
                  <LoadErrorState title={t('emergency.valuesError')} titleLevel={3} onRetry={() => void latest.refetch()} retryLabel={t('common.retry')} />
                ) : recentValues.length === 0 ? (
                  <Text color="secondary">{t('emergency.valuesNone')}</Text>
                ) : (
                  <ul className="emergency-page__list">
                    {recentValues.map((p) => {
                      const state = valueState(p);
                      return (
                        <li key={p.parameterId} className="emergency-page__row">
                          <Text as="span">
                            {label(p)}: <strong>{fmt(p.latest.value)} {p.latest.unit}</strong>
                            {p.target && ` · ${t('emergency.target', { min: fmt(p.target.min), max: fmt(p.target.max) })}`}
                            {` · ${when(p.latest.measuredAt)}`}
                          </Text>
                          {state !== 'unknown' && (
                            <Badge tone={state === 'out' ? 'warning' : 'success'}>
                              {state === 'out' ? t('emergency.outOfRange') : t('emergency.inRange')}
                            </Badge>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </Section>

            <Section title={t('emergency.changesTitle')}>
              <Card padding="lg">
                {interventions.isLoading || timeline.isLoading ? (
                  <Text>{t('emergency.changesLoading')}</Text>
                ) : changes.length === 0 ? (
                  <Text color="secondary">{t('emergency.changesNone')}</Text>
                ) : (
                  <ul className="emergency-page__list">
                    {changes.slice(0, 12).map((c) => (
                      <li key={c.id} className="emergency-page__row">
                        <Text as="span">{c.text}</Text>
                        <Text as="span" variant="caption" color="muted">
                          {when(c.at)}
                        </Text>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </Section>

            {capture?.available && (
              <VoiceRecorderButton variant="secondary" fullWidth onStart={() => capture.startRecording({ aquariumId })}>
                {t('emergency.addNote')}
              </VoiceRecorderButton>
            )}

            <Text variant="caption" color="muted">
              {t('emergency.disclaimer')}
            </Text>
          </Stack>
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        </PageContent>
      }
    />
  );
}

export default EmergencyPage;
